import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { type AuthResponse } from '@responses/auth.response';
import { StorageKeys } from '@core/utils/storage-keys.util';
import { catchError, firstValueFrom, tap, throwError, type Observable } from 'rxjs';
import { APPRoutes } from 'src/app/app.routes';
import { type ChangePasswordForm } from '@features/change-password/change-password.component';
import { PusherService } from '@services/pusher/pusher.service';
import { UserService } from '@services/user/user.service';
import { ERROR_HANDLING_CONFIG } from '@root/src/app/core/interceptors/error-handler.interceptor';
import { SettingsService } from '@core/config/settings.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly settings = inject(SettingsService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly pusherService = inject(PusherService);
  private readonly route = inject(ActivatedRoute);

  private readonly authUrl = this.settings.auth.baseUrl;
  private readonly tokenUrl = `${this.settings.auth.tokenUrl}`;

  private readonly skipErrorHandlingHeaders = new HttpHeaders({
    [ERROR_HANDLING_CONFIG.skipErrorHandlingHeader]: 'true',
  });

  async login(resp: AuthResponse): Promise<void> {
    localStorage.setItem(StorageKeys.ATO, resp.access_token);
    localStorage.setItem(StorageKeys.RTO, resp.refresh_token);
    await this.userService.getCurrentUserInfo();
    this.pusherService.initPusher();

    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    this.router.navigateByUrl(returnUrl ?? '');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(StorageKeys.ATO);
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem(StorageKeys.RTO);

    if (!refreshToken) {
      this.logout();
    }

    return this.http
      .post<AuthResponse>(
        `${this.tokenUrl}`,
        {
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: this.settings.auth.clientId,
          client_secret: this.settings.auth.clientSecret,
        },
        { headers: this.skipErrorHandlingHeaders }
      )
      .pipe(
        tap(resp => {
          localStorage.setItem(StorageKeys.ATO, resp.access_token);
          localStorage.setItem(StorageKeys.RTO, resp.refresh_token);
        }),
        catchError(err => {
          this.logout();
          return throwError(() => err);
        })
      );
  }

  loginByEmail(
    email: string,
    password: string,
    grantType: string = 'groups_password_grant'
  ): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(
        `${this.tokenUrl}`,
        {
          username: email,
          password,
          provider: this.settings.auth.provider,
          grant_type: grantType,
          client_id: this.settings.auth.clientId,
          client_secret: this.settings.auth.clientSecret,
        },
        { headers: this.skipErrorHandlingHeaders }
      )
    );
  }

  async forgotPassword(email: string): Promise<boolean> {
    return firstValueFrom(
      this.http.post<void>(`${this.authUrl}/password/forgot`, {
        email,
      })
    )
      .then(() => true)
      .catch(() => {
        // TODO: Handle error, show toast?
        return false;
      });
  }

  resetPassword(password: string, hash: string): Observable<void> {
    return this.http.post<void>(`${this.authUrl}/password/reset`, {
      password,
      hash,
    });
  }

  generateCode(phone: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.authUrl}/send-phone-login-code`, {
        phone,
        provider: this.settings.auth.provider,
      })
    );
  }

  submitCode(phone: string, code: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.http.post<AuthResponse>(
        `${this.tokenUrl}`,
        {
          phone,
          code,
          provider: this.settings.auth.provider,
          grant_type: 'groups_otp_grant',
          client_id: this.settings.auth.clientId,
          client_secret: this.settings.auth.clientSecret,
        },
        { headers: this.skipErrorHandlingHeaders }
      )
    );
  }

  changePassword(data: ChangePasswordForm): Observable<void> {
    return this.http.post<void>(`${this.authUrl}/password/change`, data);
  }

  logout(includeReturnUrl = true, explicitReturnUrl?: string): void {
    const pwaPromptDate = localStorage.getItem(StorageKeys.PWA_PROMPT_DATE);
    if (typeof _chatlio !== 'undefined') {
      _chatlio.reset();
    }
    this.pusherService.disconnect();
    localStorage.clear();
    localStorage.setItem(StorageKeys.PWA_PROMPT_DATE, pwaPromptDate ?? '');

    this.router.navigate([APPRoutes.AUTH], {
      queryParams: includeReturnUrl ? { returnUrl: explicitReturnUrl ?? this.router.url } : {},
      queryParamsHandling: 'merge',
    });
  }
}
